import type { AxiosInstance } from "axios";
import { getReplay } from "@sentry/nextjs";
import type { TranscriptLoadGateStats } from "@/components/agent-session/transcriptLoadGate";
import { dashboardRouterEvents } from "@/hooks/useDashboardRouter";
import { postFrontendMetric } from "@/lib/reportWebVital";
import {
  monotonicFromEpoch,
  startInteraction,
  type Interaction,
} from "@/lib/interactionTrace";

// In the root bundle (AuthProvider's deep-link prefetch and the snapshot
// request import it), so it imports nothing heavier than those already do.

/**
 * Critical-path phases of one session open, in order. Each phase's segment is
 * how far its mark pushed the timeline past every earlier mark, so a phase
 * that finished under an earlier one (a snapshot that landed before the view
 * mounted) is 0 and the segments sum to the total. Mirrored in
 * FrontendMetricsController::CODESMITH_SESSION_OPEN_PHASES.
 */
const PHASES = [
  "chunk",
  "view_mount",
  "snapshot",
  "commit",
  "hydrate",
  "reveal",
  "paint",
] as const;

type Phase = (typeof PHASES)[number];
type Trigger = "hard" | "select" | "auto" | "route" | "history";
type Outcome = "complete" | "hidden" | "aborted" | "error";
type CacheHit = "hit" | "inflight" | "miss";
type SnapshotWindow = { messages: number; parts: number; has_more: boolean };

interface SessionOpen {
  sessionId: string;
  interaction: Interaction;
  trigger: Trigger;
  t0: number;
  marks: Partial<Record<Phase, number>>;
  done: boolean;
  chunkCold: boolean;
  visibilityAtT0: DocumentVisibilityState;
  hiddenSince: number | null;
  hiddenMs: number;
  routeCompleteAt?: number;
  snapshotDataAt?: number;
  window?: SnapshotWindow;
  gate?: TranscriptLoadGateStats;
  revealAwaitingHydrate?: TranscriptLoadGateStats;
}

interface PendingDetach {
  sessionId: string;
  timer: ReturnType<typeof setTimeout>;
}

interface SnapshotRequest {
  start: number;
}

interface Interval {
  startTime: number;
  end: number;
}

const SNAPSHOT_URL = /\/assistant\/sessions\/([0-9a-f-]{36})(?:\?|$)/i;
const SESSION_URL = /\/sessions\/([0-9a-f-]{36})(?:[/?#]|$)/i;
const TELEMETRY_URL = "/api/frontend/";
const METRICS_URL = "/frontend/metrics";
const RING_WINDOW_MS = 120_000;
const SNAPSHOT_SESSIONS_KEPT = 50;
const PER_SESSION_KEPT = 4;
/** A back/forward render within this long of the popstate starts from it. */
const POPSTATE_WINDOW_MS = 1_000;
/** A converted `dataUpdatedAt` can land a few ms off the monotonic resource timings. */
const CLOCK_SLACK_MS = 50;

let api: AxiosInstance | null = null;
let installed = false;
let hardBegun = false;
let current: SessionOpen | null = null;
let displayedSessionId: string | null = null;
let chunkLoadedAt: number | null = null;
let lastPopStateAt = -Infinity;
let pendingDetach: PendingDetach | null = null;
let resourceObserver: PerformanceObserver | null = null;
let longTaskObserver: PerformanceObserver | null = null;
const snapshotRequests = new Map<string, SnapshotRequest[]>();
const snapshotEntries = new Map<string, PerformanceResourceTiming[]>();
let resources: PerformanceResourceTiming[] = [];
let longTasks: Interval[] = [];

function now(): number {
  return performance.now();
}

function pushCapped<K, V>(map: Map<K, V[]>, key: K, value: V, keep: number) {
  const list = map.get(key) ?? [];
  list.push(value);
  if (list.length > keep) list.shift();
  map.delete(key);
  map.set(key, list);
  if (map.size > SNAPSHOT_SESSIONS_KEPT) {
    map.delete(map.keys().next().value as K);
  }
}

function recordResource(entry: PerformanceResourceTiming) {
  if (entry.name.includes(TELEMETRY_URL)) return;
  const snapshotId = SNAPSHOT_URL.exec(entry.name)?.[1];
  if (snapshotId && !entry.name.includes("status_only")) {
    pushCapped(snapshotEntries, snapshotId, entry, PER_SESSION_KEPT);
  }
  const cutoff = now() - RING_WINDOW_MS;
  resources.push(entry);
  if (resources[0].responseEnd < cutoff) {
    resources = resources.filter((one) => one.responseEnd >= cutoff);
  }
}

function recordLongTask(entry: PerformanceEntry) {
  const cutoff = now() - RING_WINDOW_MS;
  longTasks.push({
    startTime: entry.startTime,
    end: entry.startTime + entry.duration,
  });
  if (longTasks[0].end < cutoff) {
    longTasks = longTasks.filter((one) => one.end >= cutoff);
  }
}

function observe(
  type: string,
  onEntry: (entry: PerformanceEntry) => void,
): PerformanceObserver | null {
  if (
    typeof PerformanceObserver === "undefined" ||
    !PerformanceObserver.supportedEntryTypes?.includes(type)
  ) {
    return null;
  }
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) onEntry(entry);
  });
  observer.observe({ type, buffered: true });
  return observer;
}

function onVisibilityChange() {
  if (!current) return;
  if (document.visibilityState === "hidden") {
    current.hiddenSince ??= now();
  } else if (current.hiddenSince !== null) {
    current.hiddenMs += now() - current.hiddenSince;
    current.hiddenSince = null;
  }
}

function onRouteChangeStart(url: string, options?: { shallow?: boolean }) {
  const sessionId = SESSION_URL.exec(url)?.[1];
  if (!sessionId) {
    if (current) finish(current, "aborted", "navigated");
    return;
  }
  // Shallow pushes are in-shell selections (row and link clicks), which began at the click.
  if (!options?.shallow) begin(sessionId, "route", now());
}

function onRouteChangeComplete() {
  if (current) current.routeCompleteAt ??= now();
}

/** Called once from the app root, before any open can begin. */
export function installSessionOpenTiming(nextApi: AxiosInstance): void {
  api = nextApi;
  if (installed || typeof window === "undefined") return;
  installed = true;
  resourceObserver = observe("resource", (entry) =>
    recordResource(entry as PerformanceResourceTiming),
  );
  longTaskObserver = observe("longtask", recordLongTask);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", () => {
    if (current) finish(current, "aborted", "pagehide");
  });
  window.addEventListener("popstate", () => {
    lastPopStateAt = now();
  });
  dashboardRouterEvents.on("routeChangeStart", onRouteChangeStart);
  dashboardRouterEvents.on("routeChangeComplete", onRouteChangeComplete);
}

function mark(open: SessionOpen, phase: Phase, at = now()) {
  open.marks[phase] ??= Math.max(at, open.t0);
}

function begin(sessionId: string, trigger: Trigger, t0: number) {
  if (current?.sessionId === sessionId) return;
  if (!current && displayedSessionId === sessionId) return;
  if (current) finish(current, "aborted", "superseded");
  const hidden = document.visibilityState === "hidden";
  const open: SessionOpen = {
    sessionId,
    interaction: startInteraction("codesmith.session.open", { start: t0 }),
    trigger,
    t0,
    marks: {},
    done: false,
    chunkCold: chunkLoadedAt === null || chunkLoadedAt >= t0,
    visibilityAtT0: document.visibilityState,
    hiddenSince: hidden ? t0 : null,
    hiddenMs: 0,
  };
  if (chunkLoadedAt !== null && chunkLoadedAt >= t0) {
    mark(open, "chunk", chunkLoadedAt);
  }
  current = open;
}

function openFor(sessionId: string | null): SessionOpen | null {
  return current && current.sessionId === sessionId ? current : null;
}

/** A deep-linked session on the page's own navigation: timed from navigation start. */
export function beginHardSessionOpen(sessionId: string): void {
  if (hardBegun) return;
  hardBegun = true;
  // AppProviders remount across route groups; only the document's own URL is a hard open.
  const [navigation] = performance.getEntriesByType?.("navigation") ?? [];
  if (!navigation?.name.includes(sessionId)) return;
  begin(sessionId, "hard", 0);
}

/**
 * `select` is a click on a session row or link; `auto` is the shell picking a
 * landing session; `history` is back/forward, timed from the popstate.
 */
export function beginSessionOpen(
  sessionId: string,
  trigger: "select" | "auto" | "history",
): void {
  const at = now();
  const t0 =
    trigger === "history" && at - lastPopStateAt < POPSTATE_WINDOW_MS
      ? lastPopStateAt
      : at;
  begin(sessionId, trigger, t0);
}

export function noteCodesmithChunkLoaded(): void {
  chunkLoadedAt ??= now();
  if (current) mark(current, "chunk", chunkLoadedAt);
}

export function noteSnapshotRequest(sessionId: string): void {
  pushCapped(snapshotRequests, sessionId, { start: now() }, PER_SESSION_KEPT);
}

export function markSessionOpen(
  sessionId: string,
  phase: "view_mount" | "commit" | "hydrate",
): void {
  const open = openFor(sessionId);
  if (!open) return;
  mark(open, phase);
  if (phase === "hydrate" && open.revealAwaitingHydrate) {
    reveal(open, open.revealAwaitingHydrate);
  }
}

/** `dataUpdatedAt` is the query's epoch ms, so a cached snapshot marks (and classifies) as already there. */
export function noteSessionSnapshot(
  sessionId: string,
  dataUpdatedAt: number | undefined,
  snapshotWindow: SnapshotWindow,
): void {
  const open = openFor(sessionId);
  if (!open || open.snapshotDataAt !== undefined) return;
  const dataAt =
    dataUpdatedAt === undefined || dataUpdatedAt === 0
      ? now()
      : monotonicFromEpoch(dataUpdatedAt);
  open.snapshotDataAt = dataAt;
  open.window = snapshotWindow;
  mark(open, "snapshot", dataAt);
}

/**
 * The transcript is uncovered; the open completes on the next painted frame.
 * A gate already ready lifts the cover in the transcript's own commit, from a
 * layout effect that runs before useSessionThread's passive hydrate effect,
 * so a landed snapshot's reveal waits for its hydrate to keep the phases in
 * order and time the frame that paints the hydrated thread.
 */
export function revealSessionOpen(
  sessionId: string,
  gate: TranscriptLoadGateStats,
): void {
  const open = openFor(sessionId);
  if (!open || open.marks.reveal !== undefined || open.revealAwaitingHydrate) {
    return;
  }
  if (open.snapshotDataAt !== undefined && open.marks.hydrate === undefined) {
    open.revealAwaitingHydrate = gate;
    return;
  }
  reveal(open, gate);
}

function reveal(open: SessionOpen, gate: TranscriptLoadGateStats) {
  open.revealAwaitingHydrate = undefined;
  mark(open, "reveal");
  open.gate = gate;
  const revealedSessionId = open.sessionId;
  const complete = () => {
    // A redirect between the reveal and its frame hands the open to another session.
    if (open.done || open.sessionId !== revealedSessionId) return;
    if (document.visibilityState !== "hidden") mark(open, "paint");
    finish(
      open,
      open.hiddenMs > 0 || open.hiddenSince !== null ? "hidden" : "complete",
    );
  };
  // A hidden tab runs no frames; its open is reported as hidden either way.
  if (document.visibilityState === "hidden") {
    complete();
    return;
  }
  requestAnimationFrame(() => setTimeout(complete, 0));
}

export function failSessionOpen(sessionId: string): void {
  const open = openFor(sessionId);
  if (open) finish(open, "error", "snapshot_error");
}

/**
 * The shell replaced the selected session with another (an autofix child's
 * parent, a failed fork's source): the open carries over to it, keeping its
 * trigger and start.
 */
export function redirectSessionOpen(fromId: string, toId: string): void {
  const open = openFor(fromId);
  if (!open || fromId === toId) return;
  open.sessionId = toId;
  open.marks = { chunk: open.marks.chunk, view_mount: open.marks.view_mount };
  open.snapshotDataAt = undefined;
  open.revealAwaitingHydrate = undefined;
}

function settleDetach(sessionId: string) {
  pendingDetach = null;
  if (displayedSessionId === sessionId) displayedSessionId = null;
  const open = openFor(sessionId);
  if (open) finish(open, "aborted", "unmounted");
}

/** Only StrictMode's remount of the same view may skip its pending detach. */
function cancelPendingDetach(sessionId: string) {
  if (pendingDetach === null) return;
  clearTimeout(pendingDetach.timer);
  if (pendingDetach.sessionId === sessionId) pendingDetach = null;
  else settleDetach(pendingDetach.sessionId);
}

export function attachSessionView(sessionId: string): void {
  cancelPendingDetach(sessionId);
  displayedSessionId = sessionId;
}

/** Deferred so StrictMode's unmount/remount of the same view does not abort its open. */
export function detachSessionView(sessionId: string): void {
  cancelPendingDetach(sessionId);
  pendingDetach = {
    sessionId,
    timer: setTimeout(() => settleDetach(sessionId), 0),
  };
}

function criticalPath(open: SessionOpen) {
  const phases = {} as Record<Phase, number>;
  const segments: { phase: Phase; start: number; end: number }[] = [];
  let cursor = open.t0;
  for (const phase of PHASES) {
    const at = open.marks[phase];
    const next = at === undefined ? cursor : Math.max(cursor, at);
    // Rounded cumulatively so the segments sum exactly to the rounded total.
    phases[phase] = Math.round(next - open.t0) - Math.round(cursor - open.t0);
    if (at !== undefined) segments.push({ phase, start: cursor, end: next });
    cursor = next;
  }
  return { phases, segments, end: cursor };
}

function latestRequest(sessionId: string, limit: number, since = -Infinity) {
  let best: SnapshotRequest | undefined;
  for (const request of snapshotRequests.get(sessionId) ?? []) {
    if (request.start > limit || request.start < since) continue;
    if (!best || request.start > best.start) best = request;
  }
  return best;
}

function cacheHit(open: SessionOpen): CacheHit {
  const dataAt = open.snapshotDataAt;
  if (dataAt === undefined) return "miss";
  if (dataAt <= open.t0) return "hit";
  const requestStart = latestRequest(open.sessionId, dataAt)?.start;
  return requestStart !== undefined && requestStart < open.t0
    ? "inflight"
    : "miss";
}

/** `failedAt` bounds the search for an open whose snapshot request failed. */
function snapshotEntry(open: SessionOpen, failedAt?: number) {
  const dataAt = open.snapshotDataAt ?? failedAt;
  if (dataAt === undefined) return undefined;
  // Entries arrive asynchronously, so the one for this data's request may not
  // be in yet; an earlier request's entry must not stand in for it.
  const requestStart = latestRequest(open.sessionId, dataAt)?.start;
  if (requestStart === undefined) return undefined;
  let best: PerformanceResourceTiming | undefined;
  for (const entry of snapshotEntries.get(open.sessionId) ?? []) {
    if (entry.startTime + 1 < requestStart) continue;
    if (entry.responseEnd > dataAt + CLOCK_SLACK_MS) continue;
    if (!best || entry.responseEnd > best.responseEnd) best = entry;
  }
  return best;
}

function sizeBucket(decodedBytes: number | undefined) {
  if (!decodedBytes) return "unknown";
  if (decodedBytes < 100_000) return "lt_100kb";
  if (decodedBytes < 1_000_000) return "100kb_1mb";
  if (decodedBytes < 3_000_000) return "1mb_3mb";
  return "ge_3mb";
}

function isApi(entry: PerformanceResourceTiming) {
  const base = api?.defaults?.baseURL;
  return base
    ? entry.name.startsWith(base)
    : entry.initiatorType === "xmlhttprequest";
}

function overlapCount(
  start: number,
  end: number,
  keep: (entry: PerformanceResourceTiming) => boolean,
) {
  return resources.filter(
    (entry) =>
      entry.startTime < end && entry.responseEnd > start && keep(entry),
  ).length;
}

function ms(value: number) {
  return Math.max(0, Math.round(value));
}

function fetchBreakdown(open: SessionOpen, entry: PerformanceResourceTiming) {
  // Cross-origin without Timing-Allow-Origin zeroes every phase timestamp.
  const timingAllowed = entry.requestStart > 0;
  const requestStart = latestRequest(
    open.sessionId,
    entry.startTime + 1,
  )?.start;
  const connectMs = timingAllowed
    ? entry.connectEnd - entry.domainLookupStart
    : 0;
  const server: Record<string, number> = {};
  for (const one of entry.serverTiming ?? []) {
    server[one.name.toLowerCase()] = Math.round(one.duration * 10) / 10;
  }
  return {
    queue_ms:
      requestStart === undefined ? null : ms(entry.startTime - requestStart),
    stall_ms: timingAllowed
      ? ms(entry.requestStart - entry.fetchStart - connectMs)
      : null,
    connect_ms: timingAllowed ? ms(connectMs) : null,
    ttfb_ms: timingAllowed
      ? ms(entry.responseStart - entry.requestStart)
      : null,
    download_ms: timingAllowed
      ? ms(entry.responseEnd - entry.responseStart)
      : null,
    duration_ms: ms(entry.duration),
    parse_ms:
      open.snapshotDataAt === undefined
        ? null
        : ms(open.snapshotDataAt - entry.responseEnd),
    transfer_bytes: entry.transferSize,
    encoded_bytes: entry.encodedBodySize,
    decoded_bytes: entry.decodedBodySize,
    protocol: entry.nextHopProtocol || null,
    timing_allowed: timingAllowed,
    server_ms: server,
  };
}

function longTaskStats(start: number, end: number) {
  let count = 0;
  let total = 0;
  for (const task of longTasks) {
    const overlap = Math.min(end, task.end) - Math.max(start, task.startTime);
    if (overlap <= 0) continue;
    count += 1;
    total += overlap;
  }
  return { long_task_count: count, long_task_ms: ms(total) };
}

function connection() {
  const info = (
    navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        rtt?: number;
        downlink?: number;
        saveData?: boolean;
      };
    }
  ).connection;
  return {
    effective_type: info?.effectiveType ?? null,
    rtt: info?.rtt ?? null,
    downlink: info?.downlink ?? null,
    save_data: info?.saveData ?? null,
  };
}

function finish(open: SessionOpen, outcome: Outcome, abortReason?: string) {
  if (open.done) return;
  open.done = true;
  if (current === open) current = null;

  const settled = outcome === "complete" || outcome === "hidden";
  const { phases, segments, end: lastMark } = criticalPath(open);
  const end = settled ? lastMark : now();
  if (open.hiddenSince !== null) {
    open.hiddenMs += Math.max(0, end - open.hiddenSince);
    open.hiddenSince = null;
  }
  for (const entry of resourceObserver?.takeRecords() ?? []) {
    recordResource(entry as PerformanceResourceTiming);
  }
  for (const entry of longTaskObserver?.takeRecords() ?? []) {
    recordLongTask(entry);
  }

  const mode = open.trigger === "hard" ? "hard" : "spa";
  const totalMs = Math.round(end - open.t0);
  const hit = cacheHit(open);
  const entry = snapshotEntry(open, outcome === "error" ? end : undefined);
  const size = sizeBucket(entry?.decodedBodySize);

  open.interaction.setAttributes({
    codesmith: {
      session_id: open.sessionId,
      mode,
      trigger: open.trigger,
      outcome,
      abort_reason: abortReason ?? null,
      total_ms: totalMs,
      phases_ms: phases,
      marks_ms: Object.fromEntries(
        Object.entries(open.marks).map(([phase, at]) => [
          phase,
          ms(at - open.t0),
        ]),
      ),
      route_complete_ms:
        open.routeCompleteAt === undefined
          ? null
          : ms(open.routeCompleteAt - open.t0),
      cache_hit: hit,
      size,
      chunk_cold: open.chunkCold,
      fetch: entry ? fetchBreakdown(open, entry) : null,
      window: open.window ?? null,
      gate: open.gate ?? null,
      api_inflight_at_t0: overlapCount(open.t0, open.t0, isApi),
      api_during_fetch: entry
        ? overlapCount(
            entry.startTime,
            entry.responseEnd,
            (one) => one !== entry && isApi(one),
          )
        : null,
      resources_during_fetch: entry
        ? overlapCount(
            entry.startTime,
            entry.responseEnd,
            (one) => one !== entry && !isApi(one),
          )
        : null,
      ...longTaskStats(open.t0, end),
      visibility_at_t0: open.visibilityAtT0,
      hidden_ms: ms(open.hiddenMs),
      ...connection(),
      replay_mode: getReplay()?.getRecordingMode() ?? "off",
      automated: navigator.webdriver === true,
      build_sha: process.env.NEXT_PUBLIC_BUILD_SHA ?? null,
    },
  });
  for (const { phase, start, end: segmentEnd } of segments) {
    open.interaction.addSpan({
      name: `codesmith.session.open.${phase}`,
      start,
      end: segmentEnd,
    });
  }
  open.interaction.end({
    at: end,
    error: outcome === "error" ? abortReason : undefined,
  });

  // The unloading page cancels an XHR, and a JSON post's CORS preflight with
  // it, so pagehide sends the form-encoded keepalive post instead.
  if (abortReason === "pagehide") {
    postFrontendMetric({
      "codesmith_session_open[mode]": mode,
      "codesmith_session_open[outcome]": outcome,
    });
  } else {
    void api
      ?.post(METRICS_URL, {
        codesmith_session_open:
          outcome === "complete"
            ? {
                mode,
                outcome,
                cache_hit: hit,
                size,
                total_ms: totalMs,
                phases_ms: phases,
              }
            : { mode, outcome },
      })
      .catch(() => {});
  }
}
